import { StatusType } from "./statusTypes";

/** Foreground, optional icon, and chip-background hex per theme. */
export interface StatusPaletteColors {
  main: string;
  background: string;
  icon?: string;
}

export interface StatusPaletteEntry {
  light: StatusPaletteColors;
  dark: StatusPaletteColors;
}

/**
 * Canonical status color palette - single source of truth for hex values.
 * Light/dark main colors match the spec tables; light backgrounds are chip tints.
 * Dark backgrounds are the app's own dark page background (#1E1E2F, see theme.ts) - the spec's
 * dark main colors were contrast-calibrated specifically against that exact background (its own
 * rationale column calls it "indigo base" / "cool slate" / "#1E1E2F" throughout), not a white or
 * per-hue tinted chip, so any other background would not actually deliver the stated ratios.
 */
const DARK_SURFACE = "#1E1E2F";

export const STATUS_PALETTE: Record<StatusType, StatusPaletteEntry> = {
  pass: {
    light: { main: "#16A34A", background: "#DCFCE7" },
    dark: { main: "#34D399", background: DARK_SURFACE },
  },
  fail: {
    light: { main: "#DC2626", background: "#FEE2E2" },
    dark: { main: "#FB7185", background: DARK_SURFACE },
  },
  failed_resolved: {
    light: { main: "#2563EB", background: "#DBEAFE" },
    dark: { main: "#818CF8", background: DARK_SURFACE },
  },
  due: {
    light: { main: "#EA580C", background: "#FFEDD5" },
    dark: { main: "#FB923C", background: DARK_SURFACE },
  },
  overdue: {
    light: { main: "#991B1B", background: "#FEE2E2" },
    dark: { main: "#EF4444", background: DARK_SURFACE },
  },
  missed: {
    light: { main: "#7C3AED", background: "#EDE9FE" },
    dark: { main: "#C084FC", background: DARK_SURFACE },
  },
  incomplete: {
    light: { main: "#B45309", background: "#FEF3C7" },
    dark: { main: "#FACC15", background: DARK_SURFACE },
  },
  pending: {
    light: { main: "#0D9488", background: "#CCFBF1" },
    dark: { main: "#22D3EE", background: DARK_SURFACE },
  },
  skipped: {
    // Matches "Not Signed" (StatusChip/Chip.tsx with no severity - MUI's default, unstyled Chip)
    // exactly, both text and background - see @material-ui/core/Chip/Chip.js's `styles()`:
    // backgroundColor: grey[300] light / grey[700] dark, color: getContrastText(backgroundColor).
    // Unlike every other status here, this background is NOT DARK_SURFACE in dark mode - an
    // unstyled MUI Chip's own background is grey[700] (#616161), not the page background, and
    // using DARK_SURFACE here made "Not Run" invisible against the page next to "Not Signed".
    // Icon color matches "Not Signed"'s icon separately - MUI Chip applies a hardcoded icon
    // color regardless of severity: theme.palette.grey[700]/grey[300]
    // (see @material-ui/core/Chip/Chip.js "icon" style: #616161 light / #E0E0E0 dark).
    light: { main: "rgba(0, 0, 0, 0.87)", background: "#E0E0E0", icon: "#616161" },
    dark: { main: "#ffffff", background: "#616161", icon: "#E0E0E0" },
  },
  not_due: {
    light: { main: "#16A34A", background: "#DCFCE7" },
    dark: { main: "#34D399", background: DARK_SURFACE },
  },
  complete: {
    light: { main: "#16A34A", background: "#DCFCE7" },
    dark: { main: "#34D399", background: DARK_SURFACE },
  },
  // Test History Details only (Story #12572 kept this page's own status hierarchy unchanged) -
  // its own distinct blue, separate from Skipped's grey, matching the pre-existing "lightblue"
  // treatment this chip had before the shared status library existed.
  excluded: {
    light: { main: "#1E3A8A", background: "#ADD8E6" },
    dark: { main: "#7DD3FC", background: DARK_SURFACE },
  },
};

/** Compliance + result statuses shown in palette reference legends. */
export const STATUS_PALETTE_DISPLAY_ORDER: StatusType[] = [
  "pass",
  "fail",
  "failed_resolved",
  "due",
  "overdue",
  "missed",
  "incomplete",
  "pending",
  "skipped",
];
